import { useAuth0 } from "@auth0/auth0-react";

import API_Calls_Template from "@/api/calls/API_Calls_Template";
import { useUpdateDataContext } from "@/contexts/UpdateDataContext";
import useTemplateNamesHandler from "@/hooks/useTemplateNamesHandler";

function useSaveTemplateHandler() {
  const { user } = useAuth0();
  const { handleTemplateDataChange, templateData } = useUpdateDataContext();
  const { deleteTemplate, postTemplate } = API_Calls_Template();
  const { updateTemplateNamesList } = useTemplateNamesHandler();

  const parseSaveResponse = (response) => {
    const responseParts = response.data["filename"].split("/");
    return {
      // get template name and remove .templ suffix
      name: responseParts[3].replace(new RegExp(".templ" + "$"), ""),
      created: responseParts[2],
      affiliate: responseParts[0],
    };
  };

  const handleSaveTemplate = async ({
    templateName,
    templateHasChanged,
    templateAlreadyExists,
    isPreviewSave,
    use,
  }) => {
    if (!templateHasChanged && templateAlreadyExists) {
      // don't save an unchanged template, return early
      if (isPreviewSave) {
        return {
          isSuccess: true,
          saveResponse: {
            created: templateData.created,
            template: templateData.template,
            name: templateData.name,
            affiliate: user.org_name,
            use,
          },
        };
      } else {
        return { isSuccess: true };
      }
    } else {
      return await saveTemplate(
        templateName,
        templateAlreadyExists,
        isPreviewSave,
        use
      );
    }
  };

  const saveTemplate = async (
    templateName,
    templateAlreadyExists,
    isPreviewSave,
    use
  ) => {
    const saveResponse = await postTemplate(
      user.org_name,
      templateName,
      templateData.template,
      use
    );
    if (saveResponse && !saveResponse.error && saveResponse.data) {
      return handleGoodSaveResponse(
        saveResponse,
        templateAlreadyExists,
        isPreviewSave
      );
    } else {
      return { isSucess: false, saveResponse };
    }
  };

  const handleGoodSaveResponse = (
    response,
    templateAlreadyExists,
    isPreviewSave
  ) => {
    const parsedResponse = parseSaveResponse(response);
    const saveResponse = {
      created: parsedResponse.created,
      template: templateData.template,
      name: parsedResponse.name,
      affiliate: parsedResponse.affiliate,
    };
    // delete old template if necessary
    if (templateAlreadyExists) {
      deleteOldTemplate();
    }
    // update context
    handleTemplateDataChange({
      name: saveResponse.name,
      created: saveResponse.created,
      template: templateData.template,
    });
    // udpate template names list
    updateTemplateNamesList();

    // return status or status + response
    if (isPreviewSave) {
      return {
        isSuccess: true,
        saveResponse: {
          created: saveResponse.created,
          template: templateData.template,
          name: saveResponse.name,
          affiliate: user.org_name,
        },
      };
    } else {
      return { isSuccess: true };
    }
  };

  const deleteOldTemplate = async () => {
    deleteTemplate(user.org_name, templateData.name, templateData.created).then(
      (deleteResponse) => {
        if (deleteResponse?.error) {
          console.error("Error deleting old template:", deleteResponse);
        }
      }
    );
  };

  return { handleSaveTemplate };
}

export default useSaveTemplateHandler;
